import { useQuery } from '@tanstack/react-query';
import { useCallback, useMemo, useState } from 'react';
import {
  mapSearchParamsToSearchRequest,
  QUERY_SEARCH_PARAM_SEARCHTERM
} from '~/features/catalog/mappers/catalogSearchQueryMapper';
import { getSelectedLocation } from '~/shell/utils/locationUtil.server';
import {
  getPgQuickSearchResults,
  getQuickSearchSuggestions,
  getTgQuickSearchResults
} from '~/features/catalog/article.server';
import { optionalProtectedResourceLoader } from '~/shell/utils/authUtil.server';
import { useBasePath } from '~/common/hooks/useBasePath';
import debounce from 'lodash/debounce';
import { promiseHash } from 'remix-utils/promise';
import { getSalesOrderSetFilter } from '~/shell/utils/salesOrdersetFilterUtil';
import type { AghArticle } from '~/common/types/aghArticle';
import type { BghArticle } from '~/common/types/bghArticle';
import { PgArea, TgArea } from '~/common/types/area';
import { useSalesordersetFilter } from '~/features/catalog/routes/resources/SalesOrderSetFilterRoute.tsx';

export const loader = optionalProtectedResourceLoader(async ({ api, t, request, loginInfo, area, url }) => {
  const searchRequestDto = mapSearchParamsToSearchRequest(url.searchParams);
  searchRequestDto.pageSize = 3;

  const actionExclude = loginInfo?.actionExclude ?? false;
  const hasIndividualOrderEndTimes = loginInfo?.hasIndividualOrderEndTimes ?? false;

  const isTgArea = area === TgArea;
  const isPgArea = area === PgArea;

  searchRequestDto.searchInSalesOrderSet = await getSalesOrderSetFilter(request);
  if (isPgArea) {
    searchRequestDto.ccId = await getSelectedLocation(request);
  }

  const articleSearchPromise = isTgArea
    ? getTgQuickSearchResults(api, request.signal, searchRequestDto, hasIndividualOrderEndTimes, t, actionExclude)
    : getPgQuickSearchResults(api, request.signal, searchRequestDto, t);

  const searchResult = await promiseHash({
    articleResult: articleSearchPromise,
    suggestions: getQuickSearchSuggestions(api, request.signal, searchRequestDto, isTgArea)
  });

  const suggestions = searchResult.suggestions ?? [];
  const articles: (AghArticle | BghArticle)[] = searchResult.articleResult.searchResponse.articles ?? [];

  return { suggestions, articles, searchTerm: searchRequestDto.searchTerm };
});

export function useQuickSearch() {
  const { path } = useBasePath();
  const [currentSearchTerm, setCurrentSearchTerm] = useState('');
  const { isActive } = useSalesordersetFilter();

  const debouncedSetSearchTerm = useMemo(() => debounce((term: string) => setCurrentSearchTerm(term), 300), []);

  const search = useCallback(
    (searchTerm: string) => {
      if (searchTerm.length >= 3) {
        debouncedSetSearchTerm(searchTerm);
      } else {
        setCurrentSearchTerm('');
      }
    },
    [debouncedSetSearchTerm]
  );

  const { data, isLoading } = useQuery({
    queryKey: ['quicksearch', path, currentSearchTerm, isActive],
    queryFn: async () => {
      const url = `${path}/resources/articles/quicksearch?${QUERY_SEARCH_PARAM_SEARCHTERM}=${encodeURIComponent(currentSearchTerm)}`;
      const response = await fetch(url);
      return response.json();
    },
    refetchOnWindowFocus: false,
    enabled: currentSearchTerm.length >= 3,
    staleTime: 5 * 60 * 1000
  });

  const isSearching = isLoading;

  return {
    search,
    isSearching,
    result: {
      suggestions: data?.suggestions?.map((s: any) => s.term),
      articles: data?.articles
    }
  };
}
